import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "experience-data-model-xdm-experience-events"
    }}>{`Experience Data Model (XDM) Experience Events`}</h1>
    <p>{`The Adobe Experience Platform Edge extension can send events that follow a previously defined Experience Data Model (XDM) schema to Adobe Experience Edge Network. This extension automatically adds Identity information to each Experience event as described in this document.`}</p>
    <h2 {...{
      "id": "event-structure"
    }}>{`Event structure`}</h2>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`This extension provides public APIs to build XDM Experience events; these events `}<strong parentName="p">{`should not`}</strong>{` be confused with the events that are built by using the `}<inlineCode parentName="p">{`Mobile Core`}</inlineCode>{` extension, as they are built based on XDM schemas.`}</p>
    <p>{`An XDM Experience event has the following structure:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Data`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Required`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Timestamp`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Yes`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`The timestamp when the Experience Platform event is sent to the AEP Edge mobile extension. `}<br />{` Automatically added by the SDK.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Event ID`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Yes`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Unique event identifier. `}<br />{` Automatically added by the SDK.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Event type`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`No`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`The event type, for example `}<inlineCode parentName="td">{`commerce:orderDetails`}</inlineCode>{`. Refer to `}<a parentName="td" {...{
              "href": "https://github.com/adobe/xdm/blob/master/docs/reference/classes/experienceevent.schema.md#xdmeventtype-known-values"
            }}>{`xdm:eventType Known Values`}</a>{` for more examples.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`XDM data`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Yes`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Data following the XDM schema that is defined in the Schema Editor.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Data`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`No`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`The JSON-formatted, free-form data that can be associated with an event.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Dataset ID`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`No`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Using the AEP Edge extension, you can send data to multiple datasets based on your needs and configuration in Adobe Experience Platform. When sending an experience event and providing a dataset identifier, this identifier is used for redirecting the data to the specified AEP dataset instead of the default dataset set in the datastream configuration.`}</td>
        </tr>
      </tbody>
    </table>
    <h2 {...{
      "id": "identity"
    }}>{`Identity`}</h2>
    <p>{`Here are the identity properties the AEP Edge mobile extension automatically adds to each Experience Event:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Property`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`ECID`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`The Experience Cloud Identifier (ECID)  that is generated by the Adobe Experiece Platform Mobile SDK is added to the Experience event's `}<inlineCode parentName="td">{`XDM IdentityMap`}</inlineCode>{` .`}</td>
        </tr>
      </tbody>
    </table>
    <h2 {...{
      "id": "implementation-details"
    }}>{`Implementation Details`}</h2>
    <p>{`Implementation details are automatically collected by the Edge Network extension and are sent with every Experience Event. The implementation details format is defined in `}<a parentName="p" {...{
        "href": "https://github.com/adobe/xdm/blob/master/components/datatypes/industry-verticals/implementationdetails.schema.json"
      }}>{`XDM`}</a>{`.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Property`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Version`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`The version string is a concatenation of the AEP Mobile Core extension version plus the Edge Network extension version. For example, `}<inlineCode parentName="td">{`3.3.2+1.2.0`}</inlineCode>{`.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Name`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`The name is a unique URI identifier for the AEP Mobile SDK on each supported platform. For example, `}<inlineCode parentName="td">{`https://ns.adobe.com/experience/mobilesdk/ios`}</inlineCode>{` for the Mobile SDK on iOS, or `}<inlineCode parentName="td">{`https://ns.adobe.com/experience/mobilesdk/android/reactnative`}</inlineCode>{` for the Mobile SDK on Android using the React Native wrapper.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Environment`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`For a mobile application the environment is always `}<inlineCode parentName="td">{`app`}</inlineCode>{`.`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`If you would like to include this information in your dataset, add the "Implementation Details" field group to the schema tied to your dataset.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      